import React from 'react';

export default function WeeklySection({
  weeklyPeriodText,
  weeklyWorkoutsDetail,
  weeklyCardioDetail,
  thisWeekSummary,
  lastWeekSummary,
  renderDiff,
  captureReportToImage,
  handleCopyWeeklyWorkout
}) {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div id="print-header" className="hidden">
        <h2 className="text-2xl font-black text-slate-900">WEEKLY WORKOUT SUMMARY</h2>
        <p id="print-period" className="text-sm font-bold text-slate-500 mt-1">Period: {weeklyPeriodText}</p>
      </div>

      <div id="captureArea" className="space-y-6 bg-transparent">
        <div className="bg-slate-900 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-emerald-600/10 rounded-full blur-xl"></div>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[10px] font-black text-emerald-400 tracking-wider uppercase bg-slate-800 px-2.5 py-1 rounded-md inline-block">Weekly Summary</p>
              <h3 className="text-xl font-black mt-3 tracking-tight">이번 주 요약</h3>
              <p className="text-xs text-slate-400 mt-1 font-medium">{weeklyPeriodText}</p>
            </div>
            <div className="text-right">
              <span className="material-icons-round text-3xl text-emerald-500">insights</span>
            </div>
          </div>
        </div>

        {weeklyWorkoutsDetail.length > 0 && (
          <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm animate-slideUp">
            <h4 className="text-[11px] font-black text-slate-500 mb-4 flex items-center gap-1 uppercase tracking-wider">
              <span className="material-icons-round text-sm">trending_up</span>무산소 지난주 대비 증감 요약
            </h4>
            <div className="grid grid-cols-3 divide-x divide-slate-100 text-center">
              <div>
                <p className="text-[10px] font-bold text-slate-400 mb-1">최고 중량</p>
                <div className="flex items-center justify-center">
                  <span className="text-sm font-black text-slate-800">{thisWeekSummary.maxWeight}kg</span>
                  {renderDiff(thisWeekSummary.maxWeight, lastWeekSummary.maxWeight, 'kg')}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 mb-1">총 세트</p>
                <div className="flex items-center justify-center">
                  <span className="text-sm font-black text-slate-800">{thisWeekSummary.setsCount}</span>
                  {renderDiff(thisWeekSummary.setsCount, lastWeekSummary.setsCount, '세트')}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 mb-1">총 횟수</p>
                <div className="flex items-center justify-center">
                  <span className="text-sm font-black text-slate-800">{thisWeekSummary.totalReps}</span>
                  {renderDiff(thisWeekSummary.totalReps, lastWeekSummary.totalReps, '회')}
                </div>
              </div>
            </div>
          </div>
        )}

        {weeklyWorkoutsDetail.length === 0 && weeklyCardioDetail.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center justify-center bg-slate-50/50 rounded-3xl border-2 border-dashed border-slate-200">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-4 border border-slate-100">
              <span className="material-icons-round text-2xl text-slate-400">history</span>
            </div>
            <p className="text-sm font-black text-slate-700">이번 주 운동 기록이 없습니다.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {weeklyWorkoutsDetail.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-xs font-black text-indigo-600 uppercase tracking-wider px-1">무산소 종목별 주간 요약</h4>
                {weeklyWorkoutsDetail.map((workout, index) => (
                  <div key={index} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden animate-slideUp">
                    <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                          <span className="material-icons-round text-indigo-500 text-lg">fitness_center</span>
                          {workout.name}
                        </h4>
                        <p className="text-[11px] font-bold text-slate-400 mt-1 uppercase">Weekly Best</p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-black text-indigo-600 tracking-tighter">
                          <span className="text-sm font-bold text-slate-500 mr-2">최고</span>
                          {workout.absoluteMaxWeight}<span className="text-sm font-bold text-slate-500 ml-1">kg</span>
                        </div>
                        <div className="text-xs font-bold text-slate-500 mt-0.5">× {workout.absoluteMaxReps}회</div>
                      </div>
                    </div>
                    <div className="p-5 space-y-2">
                      {workout.history.map((hist, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-2xl bg-white border border-slate-100">
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                            <span className="text-sm font-bold text-slate-600">{hist.date.slice(5).replace('-', '.')}</span>
                            <span className="text-[10px] font-bold text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-md">{hist.setsCount} Sets</span>
                          </div>
                          <div className="text-xs font-black text-slate-700">{hist.maxWeight}kg <span className="text-[10px] font-medium text-slate-400">({hist.maxReps}회)</span></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {weeklyCardioDetail.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-xs font-black text-teal-600 uppercase tracking-wider px-1">유산소 종목별 주간 요약</h4>
                {weeklyCardioDetail.map((workout, index) => (
                  <div key={index} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden animate-slideUp">
                    <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                          <span className="material-icons-round text-teal-500 text-lg">directions_run</span>
                          {workout.name}
                        </h4>
                        <p className="text-[11px] font-bold text-slate-400 mt-1 uppercase">Weekly Best Total</p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-black text-teal-600 tracking-tighter">
                          <span className="text-xl">
                            {workout.maxTotalTime > 0 ? `${workout.maxTotalTime}분 ` : ''}
                            {workout.maxTotalDistance > 0 ? `${workout.maxTotalDistance}km` : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-5 space-y-2">
                      {workout.history.map((hist, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-2xl bg-white border border-slate-100">
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                            <span className="text-sm font-bold text-slate-600">{hist.date.slice(5).replace('-', '.')}</span>
                            <span className="text-[10px] font-bold text-teal-500 bg-teal-50 px-2 py-0.5 rounded-md">{hist.setsCount} Sets</span>
                          </div>
                          <div className="text-xs font-black text-slate-700">
                            {hist.totalTime > 0 ? `${hist.totalTime}분 ` : ''}
                            {hist.totalDistance > 0 ? `${hist.totalDistance}km` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <button id="captureBtn" onClick={captureReportToImage} className="w-full mt-8 h-16 bg-emerald-600 text-white rounded-2xl font-bold flex items-center justify-center gap-2 shadow-xl shadow-emerald-100 hover:bg-emerald-700 transition-colors">
        <span className="material-icons-round text-xl">share</span>
        주간 요약 이미지 저장
      </button>

      {(weeklyWorkoutsDetail.length > 0 || weeklyCardioDetail.length > 0) && (
        <button
          onClick={handleCopyWeeklyWorkout}
          className="w-full mt-3 h-16 bg-slate-800 text-white rounded-2xl font-bold flex items-center justify-center gap-2 shadow-xl shadow-slate-200 hover:bg-slate-700 transition-colors"
        >
          <span className="material-icons-round text-xl">content_copy</span>
          주간 운동 기록 복사
        </button>
      )}
    </div>
  );
}